/**
 * useServiceNudges - Universal hook for contextual service nudges across all job categories
 * Tracks user behavior and triggers relevant service prompts
 */
import { useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category-specific nudge configurations
const CATEGORY_NUDGES = {
  // ===== FREELANCE & GIGS =====
  'freelance-gigs': {
    browse_threshold: 5,
    nudges: {
      browse: {
        icon: '🧑‍💻',
        message: 'Clients compare profiles fast.',
        subtitle: 'A polished profile can increase your response rate by 40%',
        cta: 'Improve my chances',
        serviceId: 'profile_optimisation',
        price: 35,
      },
      first_apply: {
        icon: '✨',
        message: 'Great! You applied to your first gig.',
        subtitle: 'Make sure your profile stands out from other applicants',
        cta: 'Optimise profile',
        serviceId: 'profile_optimisation',
        price: 35,
      },
      inline: {
        icon: '✨',
        message: 'Stand out to clients',
        subtitle: 'Profile Optimisation',
        cta: 'Improve chances',
        serviceId: 'profile_optimisation',
        price: 35,
      },
    },
  },
  
  // ===== CONSTRUCTION & PROPERTY =====
  'construction-property': {
    browse_threshold: 4,
    nudges: {
      browse: {
        icon: '🏗️',
        message: 'Most construction roles require CSCS cards.',
        subtitle: 'Get site-ready with proper certification guidance',
        cta: 'Get CSCS ready',
        serviceId: 'cscs_card_help',
        price: 45,
      },
      first_apply: {
        icon: '👷',
        message: 'Applied for your first construction role!',
        subtitle: 'Make sure you have the right certifications',
        cta: 'Check requirements',
        serviceId: 'cscs_card_help',
        price: 45,
      },
      inline: {
        icon: '🛡️',
        message: 'Site-ready candidates get hired faster',
        subtitle: 'CSCS Card Help',
        cta: 'Get certified',
        serviceId: 'cscs_card_help',
        price: 45,
      },
    },
  },
  
  // ===== HOSPITALITY =====
  'hospitality': {
    browse_threshold: 4,
    nudges: {
      browse: {
        icon: '🍽️',
        message: 'Food hygiene certification opens more doors.',
        subtitle: 'Most hospitality employers require Level 2 certification',
        cta: 'Get certified',
        serviceId: 'food_hygiene_cert',
        price: 25,
      },
      first_apply: {
        icon: '🏨',
        message: 'Great start in hospitality!',
        subtitle: 'Food hygiene certification can boost your chances',
        cta: 'Get certified',
        serviceId: 'food_hygiene_cert',
        price: 25,
      },
      inline: {
        icon: '✅',
        message: 'Certified candidates get priority',
        subtitle: 'Food Hygiene Certification',
        cta: 'Get certified',
        serviceId: 'food_hygiene_cert',
        price: 25,
      },
    },
  },
  
  // ===== RETAIL =====
  'retail': {
    browse_threshold: 5,
    nudges: {
      browse: {
        icon: '🛍️',
        message: 'Customer service skills make the difference.',
        subtitle: 'Stand out with interview coaching',
        cta: 'Prepare now',
        serviceId: 'interview_coaching',
        price: 35,
      },
      first_apply: {
        icon: '🛒',
        message: 'Applied to your first retail position!',
        subtitle: 'Interview coaching can help you land the role',
        cta: 'Get coached',
        serviceId: 'interview_coaching',
        price: 35,
      },
      inline: {
        icon: '💬',
        message: 'Ace your retail interview',
        subtitle: 'Interview Coaching',
        cta: 'Get coached',
        serviceId: 'interview_coaching',
        price: 35,
      },
    },
  },
  
  // ===== MANUFACTURING & WAREHOUSE =====
  'manufacturing-warehouse': {
    browse_threshold: 4,
    nudges: {
      browse: {
        icon: '🏭',
        message: 'Forklift certification gets you hired faster.',
        subtitle: 'Many warehouse roles require FLT certification',
        cta: 'Get FLT ready',
        serviceId: 'forklift_training',
        price: 55,
      },
      first_apply: {
        icon: '📦',
        message: 'Applied for a warehouse role!',
        subtitle: 'FLT certification opens more opportunities',
        cta: 'Check training',
        serviceId: 'forklift_training',
        price: 55,
      },
      inline: {
        icon: '🚜',
        message: 'FLT certified = more opportunities',
        subtitle: 'Forklift Training',
        cta: 'Get certified',
        serviceId: 'forklift_training',
        price: 55,
      },
    },
  },
  
  // ===== LOGISTICS & TRANSPORT =====
  'logistics-transport': {
    browse_threshold: 4,
    nudges: {
      browse: {
        icon: '🚚',
        message: 'CPC & Driver compliance matters.',
        subtitle: 'Make sure your documentation is up to date',
        cta: 'Check compliance',
        serviceId: 'driver_compliance',
        price: 40,
      },
      first_apply: {
        icon: '🚛',
        message: 'Applied for a driving role!',
        subtitle: 'Ensure your CPC and documentation are ready',
        cta: 'Get compliant',
        serviceId: 'driver_compliance',
        price: 40,
      },
      inline: {
        icon: '📋',
        message: 'Compliant drivers get hired faster',
        subtitle: 'Driver Compliance Check',
        cta: 'Check now',
        serviceId: 'driver_compliance',
        price: 40,
      },
    },
  },
  
  // ===== CREATIVE & MEDIA =====
  'creative-media': {
    browse_threshold: 5,
    nudges: {
      browse: {
        icon: '🎨',
        message: 'Your portfolio is your calling card.',
        subtitle: 'A strong portfolio gets you noticed',
        cta: 'Review portfolio',
        serviceId: 'portfolio_review',
        price: 45,
      },
      first_apply: {
        icon: '✨',
        message: 'Applied for a creative role!',
        subtitle: 'Make sure your portfolio stands out',
        cta: 'Get feedback',
        serviceId: 'portfolio_review',
        price: 45,
      },
      inline: {
        icon: '🖼️',
        message: 'Strong portfolios win creative roles',
        subtitle: 'Portfolio Review',
        cta: 'Get reviewed',
        serviceId: 'portfolio_review',
        price: 45,
      },
    },
  },
  
  // ===== SECURITY =====
  'security': {
    browse_threshold: 4,
    nudges: {
      browse: {
        icon: '🛡️',
        message: 'SIA license is essential for security roles.',
        subtitle: 'Get licensed and start working faster',
        cta: 'Get SIA ready',
        serviceId: 'sia_license_help',
        price: 50,
      },
      first_apply: {
        icon: '🔒',
        message: 'Applied for a security position!',
        subtitle: 'Make sure your SIA license is valid',
        cta: 'Check license',
        serviceId: 'sia_license_help',
        price: 50,
      },
      inline: {
        icon: '🪪',
        message: 'Licensed security = instant hire',
        subtitle: 'SIA License Help',
        cta: 'Get licensed',
        serviceId: 'sia_license_help',
        price: 50,
      },
    },
  },
  
  // ===== CLEANING & DOMESTIC =====
  'cleaning-domestic': {
    browse_threshold: 5,
    nudges: {
      browse: {
        icon: '🧹',
        message: 'DBS checks speed up your hiring.',
        subtitle: 'Many cleaning roles require background checks',
        cta: 'Get DBS ready',
        serviceId: 'dbs_check_help',
        price: 35,
      },
      first_apply: {
        icon: '✨',
        message: 'Applied for a cleaning role!',
        subtitle: 'DBS checks are often required - be prepared',
        cta: 'Check requirements',
        serviceId: 'dbs_check_help',
        price: 35,
      },
      inline: {
        icon: '📋',
        message: 'DBS-ready candidates get hired faster',
        subtitle: 'DBS Check Help',
        cta: 'Get ready',
        serviceId: 'dbs_check_help',
        price: 35,
      },
    },
  },
  
  // ===== DEFAULT (for any category without specific config) =====
  'default': {
    browse_threshold: 5,
    nudges: {
      browse: {
        icon: '💼',
        message: 'Stand out from other candidates.',
        subtitle: 'Professional preparation increases your chances',
        cta: 'Get prepared',
        serviceId: 'cv_review',
        price: 30,
      },
      first_apply: {
        icon: '✨',
        message: 'Great! You applied for a role.',
        subtitle: 'Make sure your CV makes the right impression',
        cta: 'Review CV',
        serviceId: 'cv_review',
        price: 30,
      },
      inline: {
        icon: '📄',
        message: 'A strong CV opens doors',
        subtitle: 'CV Review',
        cta: 'Get reviewed',
        serviceId: 'cv_review',
        price: 30,
      },
    },
  },
};

// Resolve category aliases
const resolveCategory = (category) => {
  const aliases = {
    'hospitality-tourism': 'hospitality',
    'construction': 'construction-property',
    'warehouse': 'manufacturing-warehouse',
    'manufacturing': 'manufacturing-warehouse',
    'creative': 'creative-media',
    'media': 'creative-media',
    'logistics': 'logistics-transport',
    'transport': 'logistics-transport',
    'driver': 'logistics-transport',
    'delivery': 'logistics-transport',
    'freelance': 'freelance-gigs',
    'gigs': 'freelance-gigs',
  };
  return aliases[category] || category;
};

/**
 * useServiceNudges Hook
 * @param {string} category - Job category slug
 * @returns {object} Nudge state and handlers
 */
export const useServiceNudges = (category) => {
  const resolvedCategory = resolveCategory(category);
  const config = CATEGORY_NUDGES[resolvedCategory] || CATEGORY_NUDGES['default'];
  
  const [viewCount, setViewCount] = useState(0);
  const [applyCount, setApplyCount] = useState(0);
  const [currentNudge, setCurrentNudge] = useState(null);
  const [dismissedNudges, setDismissedNudges] = useState([]);
  const [showServiceSheet, setShowServiceSheet] = useState(false);
  
  // Check if nudge should be shown
  const checkNudge = useCallback((type, force = false) => {
    if (dismissedNudges.includes(type)) return;
    
    const nudge = config.nudges[type];
    if (!nudge) return;
    
    let shouldShow = force;
    
    if (type === 'browse') {
      shouldShow = viewCount >= config.browse_threshold;
    } else if (type === 'first_apply') {
      shouldShow = applyCount === 1;
    }
    
    if (shouldShow) {
      setCurrentNudge({ type, ...nudge, category: resolvedCategory });
    }
  }, [viewCount, applyCount, dismissedNudges, config, resolvedCategory]);
  
  // Track job view
  const trackView = useCallback(() => {
    setViewCount(prev => {
      const newCount = prev + 1;
      if (newCount === config.browse_threshold) {
        setTimeout(() => checkNudge('browse', true), 500);
      }
      return newCount;
    });
  }, [config.browse_threshold, checkNudge]);
  
  // Track application
  const trackApply = useCallback(() => {
    setApplyCount(prev => {
      const newCount = prev + 1;
      if (newCount === 1) {
        setTimeout(() => checkNudge('first_apply', true), 1000);
      }
      return newCount;
    });
  }, [checkNudge]);
  
  // Dismiss current nudge
  const dismissNudge = useCallback(() => {
    if (currentNudge) {
      setDismissedNudges(prev => [...prev, currentNudge.type]);
    }
    setCurrentNudge(null);
  }, [currentNudge]);
  
  // Handle nudge action (open service)
  const handleNudgeAction = useCallback(() => {
    setShowServiceSheet(true);
    dismissNudge();
  }, [dismissNudge]);
  
  // Get inline nudge config (for showing after N listings)
  const getInlineNudge = useCallback((position) => {
    // Show inline nudge after position 3 and 8
    if ((position === 3 || position === 8) && !dismissedNudges.includes('inline')) {
      return { ...config.nudges.inline, category: resolvedCategory };
    }
    return null;
  }, [config.nudges.inline, dismissedNudges, resolvedCategory]);
  
  // Dismiss inline nudge
  const dismissInlineNudge = useCallback(() => {
    setDismissedNudges(prev => [...prev, 'inline']);
  }, []);
  
  // Reset state when category changes
  useEffect(() => {
    setViewCount(0);
    setApplyCount(0);
    setCurrentNudge(null);
    setDismissedNudges([]);
  }, [category]);
  
  return {
    // State
    viewCount,
    applyCount,
    currentNudge,
    showServiceSheet,
    
    // Handlers
    trackView,
    trackApply,
    dismissNudge,
    handleNudgeAction,
    setShowServiceSheet,
    
    // Inline nudge
    getInlineNudge,
    dismissInlineNudge,
    
    // Config
    config: config.nudges,
    category: resolvedCategory,
  };
};

export default useServiceNudges;
export { CATEGORY_NUDGES, resolveCategory };
